import React from "react";
import { Instagram, Facebook, MessageCircle, MapPin, Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { buildWhatsAppUrl, INSTAGRAM_URL, FACEBOOK_URL } from "@/data/translations";
import {
  CONTACTS,
  OFFICE_ADDRESS_EN,
  OFFICE_ADDRESS_AR,
  OFFICE_ADDRESS_FR,
  OFFICE_MAP_URL,
  OFFICIAL_EMAIL,
} from "@/data/contacts";
import DeveloperCredit from "@/components/DeveloperCredit";
import { LOGO_DARK } from "@/data/branding";

const LOGO_BLACK_BG = LOGO_DARK;

export default function Footer() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  const quickLinks = [
    { href: "#home", label: t.nav.home },
    { href: "#how", label: t.nav.howItWorks },
    { href: "#countries", label: t.nav.countries },
    { href: "#rates", label: t.nav.rates },
    { href: "#why", label: t.nav.why },
    { href: "#about", label: t.nav.team },
    { href: "#faq", label: t.nav.faq },
  ];
  const legalLabel = lang === "ar" ? "القانونية" : lang === "fr" ? "Légal" : "Legal";
  const legalLinks = [
    { to: "/terms", label: lang === "ar" ? "الشروط" : lang === "fr" ? "Conditions" : "Terms" },
    { to: "/privacy", label: lang === "ar" ? "الخصوصية" : lang === "fr" ? "Confidentialité" : "Privacy" },
    { to: "/prohibited-items", label: lang === "ar" ? "الممنوعات" : lang === "fr" ? "Articles interdits" : "Prohibited Items" },
  ];

  const handleClick = (e, href) => {
    e.preventDefault();
    if (!onHome) {
      navigate("/" + href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const officeAddress =
    lang === "ar" ? OFFICE_ADDRESS_AR : lang === "fr" ? OFFICE_ADDRESS_FR : OFFICE_ADDRESS_EN;

  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="bg-[#0A0A0A] text-white pt-16 md:pt-20 pb-8 md:pb-10 overflow-hidden relative"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[200px] bg-[#F37021]/10 blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 md:gap-9 lg:gap-10 pb-10 md:pb-14 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_BLACK_BG} alt="BOB Services" className="h-14 md:h-16 w-auto" />
            </div>
            <p className="text-[#B5B5B5] text-sm leading-7 max-w-sm mb-5">
              {t.footer.tagline}
            </p>
            <div className="flex flex-wrap items-center gap-2.5">
              <a
                href={buildWhatsAppUrl(t.common.whatsappMsg)}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome-focus-ring inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-white text-sm font-medium transition-all shadow-[0_10px_24px_rgba(37,211,102,0.2)]"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome-focus-ring w-11 h-11 rounded-full border border-white/15 hover:bg-[#F37021] hover:border-[#F37021] flex items-center justify-center transition-all"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="chrome-focus-ring w-11 h-11 rounded-full border border-white/15 hover:bg-[#F37021] hover:border-[#F37021] flex items-center justify-center transition-all"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-5">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => handleClick(e, l.href)}
                    className="chrome-focus-ring text-sm text-[#D4D4D4] hover:text-[#F37021] transition-colors"
                    data-testid={`footer-link-${l.href.replace('#', '')}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-7 pt-6 border-t border-white/10">
              <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-4">
                {legalLabel}
              </h4>
              <ul className="space-y-3">
                {legalLinks.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="chrome-focus-ring text-sm text-[#D4D4D4] hover:text-[#F37021] transition-colors"
                      data-testid={`footer-legal-${l.to.replace("/", "")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contacts */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-5">
              {t.footer.contacts}
            </h4>
            <ul className="space-y-3">
              {CONTACTS.map((c) => (
                <li key={c.id}>
                  <a
                    href={buildWhatsAppUrl(t.common.whatsappMsg, c.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="chrome-focus-ring group flex items-center gap-3 text-sm text-[#D4D4D4] hover:text-white transition-colors"
                    data-testid={`footer-contact-${c.id}`}
                  >
                    <span className="w-10 h-10 rounded-full border border-white/10 bg-white/5 flex items-center justify-center shrink-0 group-hover:border-[#25D366] group-hover:bg-[#25D366]/10 transition-colors">
                      <MessageCircle className="w-4 h-4 text-[#25D366]" />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-[11px] tracking-wider uppercase text-[#888]">{c.label}</span>
                      <span className="font-medium" dir="ltr">{c.display}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Office + Email */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-5">
              {t.footer.office}
            </h4>
            <a
              href={OFFICE_MAP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="chrome-focus-ring group block mb-5 rounded-2xl"
              data-testid="footer-office-link"
            >
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#F37021] mt-0.5 shrink-0" />
                <div className="text-sm text-[#D4D4D4] leading-relaxed group-hover:text-white transition-colors">
                  {officeAddress}
                  <div className="mt-1.5 text-xs text-[#F37021] font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    {t.footer.viewOnMap}
                    <span aria-hidden>→</span>
                  </div>
                </div>
              </div>
            </a>

            <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-3">
              {t.footer.email}
            </h4>
            <a
              href={`mailto:${OFFICIAL_EMAIL}`}
              className="chrome-focus-ring inline-flex items-center gap-2 text-sm text-[#D4D4D4] hover:text-white transition-colors"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4 text-[#F37021]" />
              {OFFICIAL_EMAIL}
            </a>

            <h4 className="text-xs font-semibold tracking-[0.25em] uppercase text-[#F37021] mb-3 mt-5">
              {t.footer.address}
            </h4>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="chrome-focus-ring flex items-center gap-2 text-sm text-[#D4D4D4] hover:text-white transition-colors w-fit"
              data-testid="footer-instagram-link"
            >
              <Instagram className="w-4 h-4 text-[#F37021]" />
              @bobserviceslb
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="chrome-focus-ring flex items-center gap-2 text-sm text-[#D4D4D4] hover:text-white transition-colors mt-2 w-fit"
              data-testid="footer-facebook-link"
            >
              <Facebook className="w-4 h-4 text-[#F37021]" />
              BOB Services LB
            </a>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="hidden md:block pt-12 pb-4 select-none pointer-events-none">
          <div className="font-display font-black text-[16vw] leading-none tracking-tighter text-center bg-gradient-to-b from-white/[0.08] to-white/[0.02] bg-clip-text text-transparent">
            BOB SERVICES
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-5 border-t border-white/10 text-xs text-[#737373]">
          <p>© {new Date().getFullYear()} BOB Services LB. {t.footer.rights}</p>
          <p>{t.footer.madeIn}</p>
        </div>

        {/* Developer credit */}
        <DeveloperCredit />
      </div>
    </footer>
  );
}
